import { FoodsSelectServerPagination, Typography } from "@foodpilot/foods";
import { Box, Stack, useTheme } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetAllTagTypes } from "src/api/client-api/tag/useGetAllTagTypes";
import { useGetAllTagValues } from "src/api/client-api/tag/useGetAllTagValues";
import { RemovableTag } from "src/components/AssociatedStructures";
import { FoodsFieldSkeleton } from "src/components/EntityForm/FoodsFields";
import { FoodsSelectTriggerAdapter } from "src/components/EntityForm/FoodsSelectTriggerAdapter";
import type { TagContext, TagType, TagValue } from "src/types/tag";

import { resolveTagColors } from "./tagColors";

type EntityTagFieldV2Props = {
    context: TagContext;
    tags?: Record<string, string[]>;
    onTagsChange: (tags: Record<string, string[]>) => void;
};

export const EntityTagFieldV2 = (props: EntityTagFieldV2Props) => {
    const { context, onTagsChange, tags = {} } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const [searchByType, setSearchByType] = useState<Record<string, string>>({});

    const { tagTypes = [], isLoading: tagTypesLoading } = useGetAllTagTypes({
        context,
        itemsPerPage: 100,
        active: true,
    });

    const { data: tagValuesResponse } = useGetAllTagValues({ itemsPerPage: 1000 });
    const allTagValues: TagValue[] = tagValuesResponse?.tagValues ?? [];

    const slugToValue = allTagValues.reduce<Record<string, TagValue>>((valuesBySlug, value) => {
        valuesBySlug[value.slug] = value;
        return valuesBySlug;
    }, {});

    const handleAddValue = (tagType: TagType, value: TagValue) => {
        const next = { ...tags };

        if (tagType.multiple === false) {
            next[tagType.slug] = [value.slug];
        } else {
            const existing = next[tagType.slug] ?? [];
            if (existing.includes(value.slug)) return;
            next[tagType.slug] = [...existing, value.slug];
        }

        onTagsChange(next);
    };

    const handleRemoveValue = (typeSlug: string, valueSlug: string) => {
        const next = { ...tags };
        if (!next[typeSlug]) return;

        next[typeSlug] = next[typeSlug].filter((slug) => slug !== valueSlug);
        if (next[typeSlug].length === 0) delete next[typeSlug];
        onTagsChange(next);
    };

    if (tagTypesLoading) {
        return <FoodsFieldSkeleton label={t("product.v2.tags.loading", "Loading tags")} height={theme.spacing(17.5)} />;
    }

    if (tagTypes.length === 0) {
        return (
            <Typography variant="body-regular" sx={{ color: theme.custom.grey[2000] }}>
                {t("tags.messages.no_tag_types_available", "No tag types available")}
            </Typography>
        );
    }

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                columnGap: theme.spacing(2),
                rowGap: theme.spacing(2),
            }}
        >
            {tagTypes.map((tagType) => {
                const search = searchByType[tagType.slug] ?? "";
                const selectedSlugs = tags[tagType.slug] ?? [];
                const selectedValues = selectedSlugs.reduce<TagValue[]>((values, slug) => {
                    const tagValue = slugToValue[slug];
                    if (tagValue) values.push(tagValue);
                    return values;
                }, []);
                const remainingValues = allTagValues.filter(
                    (value) => value.tagType?.id === tagType.id && !selectedSlugs.includes(value.slug),
                );
                const placeholder = t("product.v2.tags.add", "Add a {{label}}", { label: tagType.name });

                return (
                    <Stack key={tagType.id} sx={{ gap: theme.spacing(1), minWidth: 0 }}>
                        <Typography variant="body-semibold" sx={{ color: theme.custom.grey[2800] }}>
                            {tagType.name}
                        </Typography>

                        {selectedValues.length > 0 && (
                            <Stack direction="row" gap={theme.spacing(1)} flexWrap="wrap">
                                {selectedValues.map((value) => {
                                    const colors = resolveTagColors(theme, tagType, value);
                                    return (
                                        <RemovableTag
                                            key={`${tagType.slug}-${value.slug}`}
                                            label={value.name}
                                            backgroundColor={colors.background}
                                            foregroundColor={colors.foreground}
                                            removeLabel={t("product.v2.tags.remove", "Remove {{name}}", {
                                                name: value.name,
                                            })}
                                            onRemove={() => handleRemoveValue(tagType.slug, value.slug)}
                                        />
                                    );
                                })}
                            </Stack>
                        )}

                        <FoodsSelectTriggerAdapter accessibleLabel={tagType.name}>
                            <FoodsSelectServerPagination
                                options={remainingValues}
                                selectedOption={null}
                                getId={(item) => item?.id ?? ""}
                                getName={(item) => item?.name ?? ""}
                                onChange={(value) => {
                                    if (value) handleAddValue(tagType, value);
                                }}
                                textOptions={{
                                    placeholder,
                                    searchPlaceholder: t("component.select.searchplaceholder"),
                                    emptyOptionsText: t("component.select.nooptions"),
                                    defaultValueText: placeholder,
                                    noResult: t("component.select.noresultfound"),
                                }}
                                search={search}
                                setSearch={(value) =>
                                    setSearchByType((current) => ({ ...current, [tagType.slug]: value }))
                                }
                                isSearchFound={(value) =>
                                    search === "" || value.toLowerCase().includes(search.toLowerCase())
                                }
                                BoxProps={{ width: "100%" }}
                            />
                        </FoodsSelectTriggerAdapter>
                    </Stack>
                );
            })}
        </Box>
    );
};
